import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  samplesApi,
  doctorsApi,
  assignmentsApi,
  comparisonApi,
  getErrorMessage,
  getMediaUrl,
  markNotificationsForOpenContext,
  type ApiUser,
  type Assignment,
  type ComparisonOverview,
  type Patch,
} from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Eye, UserMinus } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ImagePreviewDialog } from '@/components/ImagePreviewDialog';
import { ComparisonReconciliationCard } from '@/components/ComparisonReconciliationCard';

function unwrapDoctors(data: { results?: ApiUser[] } | ApiUser[]): ApiUser[] {
  if (Array.isArray(data)) return data;
  return data.results ?? [];
}

export function SampleDetail() {
  const { sampleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sample, setSample] = useState<Awaited<ReturnType<typeof samplesApi.detail>>['data'] | null>(null);
  const [patches, setPatches] = useState<Patch[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [doctors, setDoctors] = useState<ApiUser[]>([]);
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [adminNote, setAdminNote] = useState('');
  const [previewPatch, setPreviewPatch] = useState<Patch | null>(null);
  const [comparison, setComparison] = useState<ComparisonOverview | null>(null);

  const load = useCallback(async () => {
    if (!sampleId) return;
    setLoading(true);
    try {
      const [{ data: sampleData }, { data: patchData }, { data: assignData }, { data: doctorData }] = await Promise.all([
        samplesApi.detail(sampleId),
        samplesApi.patches(sampleId),
        samplesApi.assignments(sampleId),
        doctorsApi.list(),
      ]);
      setSample(sampleData);
      setPatches(
        Array.isArray(patchData)
          ? patchData
          : ((patchData as { results?: Patch[] })?.results ?? []),
      );
      setAssignments(Array.isArray(assignData) ? assignData : []);
      setDoctors(unwrapDoctors(doctorData).filter((d) => d.is_active));

      if (sampleData.status === 'reconciling' || sampleData.status === 'completed') {
        try {
          const { data: comp } = await comparisonApi.overview(sampleId);
          setComparison(comp);
        } catch {
          setComparison(null);
        }
      } else {
        setComparison(null);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [sampleId, navigate]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!sampleId) return;
    void markNotificationsForOpenContext({ sample_id: sampleId });
  }, [sampleId]);

  const savedAdminNote = assignments.find((a) => a.admin_note?.trim())?.admin_note?.trim() ?? '';

  const handleAssign = async () => {
    if (!sampleId || !selectedDoctors.length) return;
    try {
      const noteToAssign = adminNote.trim() || savedAdminNote;
      await samplesApi.assign(sampleId, { doctor_ids: selectedDoctors, admin_note: noteToAssign });
      toast.success('Doctors assigned');
      setSelectedDoctors([]);
      setAdminNote('');
      void load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleUnassign = async (assignmentId: string) => {
    try {
      await assignmentsApi.unassign(assignmentId);
      toast.success('Doctor unassigned');
      void load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (loading || !sample) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const assignedDoctorIds = new Set(assignments.map((a) => a.doctor_id));

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={sample.sample_name}
        subtitle={`${sample.total_patches} patches · ${format(new Date(sample.upload_date), 'MMM d, yyyy')}`}
        backTo="/admin"
        actions={<StatusBadge status={sample.status} variant="admin" />}
      />
      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <Card>
          <CardHeader><CardTitle>Patch grid</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {patches.map((patch) => (
                <button
                  key={patch.id}
                  type="button"
                  className="border rounded-lg overflow-hidden hover:ring-2 ring-blue-400 text-left"
                  onClick={() => setPreviewPatch(patch)}
                >
                  <img src={getMediaUrl(patch.image_path)} alt={patch.filename} className="w-full aspect-video object-cover" />
                  <div className="p-2 text-xs font-medium truncate">{patch.patch_identifier}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Assign doctors</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {doctors.filter((d) => !assignedDoctorIds.has(d.id)).map((doc) => (
                <label key={doc.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedDoctors.includes(doc.id)}
                    onCheckedChange={(checked) => {
                      setSelectedDoctors((prev) =>
                        checked ? [...prev, doc.id] : prev.filter((id) => id !== doc.id),
                      );
                    }}
                  />
                  <span>{doc.full_name} ({doc.email})</span>
                </label>
              ))}
            </div>
            {savedAdminNote ? (
              <div className="rounded-md border bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-700">Admin note</p>
                <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{savedAdminNote}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Admin note (optional)</Label>
                <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Notes for the reviewing doctor…" />
              </div>
            )}
            <Button onClick={handleAssign} disabled={!selectedDoctors.length}>Assign selected</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assignments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {assignments.length === 0 ? (
              <p className="text-sm text-gray-500">No assignments yet</p>
            ) : (
              assignments.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-4">
                  <div>
                    <p className="font-medium">{a.doctor_name}</p>
                    <StatusBadge status={a.status} />
                    {a.is_final && <Badge className="ml-2">Final</Badge>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/samples/${sampleId}/reviews/${a.id}`)}>
                      <Eye className="size-4 mr-1" /> View
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="destructive">
                          <UserMinus className="size-4 mr-1" /> Unassign
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Unassign {a.doctor_name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes the assignment from {a.doctor_name}, along with any
                            saved reviews and annotations they made for this sample. This action
                            cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleUnassign(a.id)}>
                            Unassign
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {(sample.status === 'reconciling' || sample.status === 'completed') && (
          <ComparisonReconciliationCard
            mode="admin"
            sampleId={sampleId}
            comparison={comparison}
            sampleName={sample.sample_name}
            sampleStatus={sample.status}
            reconciledAt={sample.reconciled_at}
            onReload={load}
          />
        )}
      </main>

      {previewPatch && (
        <ImagePreviewDialog
          imageUrl={getMediaUrl(previewPatch.image_path)}
          onClose={() => setPreviewPatch(null)}
          title={previewPatch.filename}
        />
      )}
    </div>
  );
}
